/**
 * 官网站点内容数据
 * 四大业务线：智慧农业 / 工业物联网 / MES 生产执行系统 / 企业服务
 *
 * 说明：所有对外文案、指标均集中在此文件，便于维护。
 * 带 `TODO(待补充)` 注释的字段是经营数据位，需要业务方填真实数字后再上线。
 */

export const company = {
  shortName: '皓辰科技',
  name: '海阳皓辰网络科技有限公司',
  brandEn: 'HOYOTECH',
  slogan: '让产业现场长出智能',
  intro:
    '海阳皓辰网络科技有限公司是一家面向农业、工业与政企客户的产业数字化服务商。' +
    '我们以物联网终端、边缘控制与数据智能平台为核心，提供从现场感知、设备接入到业务应用的一体化解决方案，' +
    '让数据在生产现场真正跑起来。',
  phone: '15587362026',
  email: 'baoyikun@hoyotech.top',
  address: '山东省烟台市海阳市经发商务中心 609 室',
  addressShort: '海阳市经发商务中心 609 室',
  icp: '鲁ICP备2025191643号',
  /** 工信部备案查询入口（页脚备案号按规范可点击跳转） */
  icpUrl: 'https://beian.miit.gov.cn/',
  founded: '2025',
  /** 成立日期（用户确认：2025 年 6 月 9 日） */
  foundedFull: '2025 年 6 月 9 日',
  foundedISO: '2025-06-09',
  /**
   * 微信/企业微信咨询入口。
   *
   * ⚠️ 这里**没有 search 字段，是故意的**：公司手机号 15587362026 无法通过微信搜索添加
   * （用户 2026-10-13 明确确认），所以不能再用「微信搜索手机号」这类指引——
   * 那会让访客搜不到人，等于给了一条走不通的路。唯一的线上加好友入口是扫码。
   *
   * 二维码为企业微信「海阳皓辰网络科技有限公司 / 包已坤」的名片码，解码结果为
   * https://work.weixin.qq.com/u/vc20afac5010d7f861?v=5.0.11.10000&bb=ff53c43141
   * 已用 macOS Vision（VNDetectBarcodesRequest）验证可识别，含缩到 176px 显示尺寸后的复验。
   * 若日后重新启用「可搜索的微信号」，再加回 search 字段并在各页面接上。
   */
  wechat: {
    label: '企业微信',
    value: '扫码添加',
    tip: '手机不便扫码时，请直接致电',
    qr: 'gen/wx-qr.png'
  }
}

/**
 * 在线演示环境（公司级）：所有业务线共用
 * 演示地址对外公开，演示账号/密码需要联系我方获取。
 */
export const demo = {
  url: 'https://www.hoyotech.top/',
  label: '在线演示环境',
  title: '先看真实系统，再谈方案',
  desc:
    '皓辰自研的产业物联网平台对外开放演示环境，覆盖设备接入、实时监控、远程控制、' +
    '自动规则引擎、告警工单与数据看板等核心模块，可用真实交互流程验证能力边界。',
  points: [
    '浏览器直接访问，无需安装部署',
    '演示环境含农业与工业两类真实场景数据',
    '可结合你的现场场景与设备清单做定向演示'
  ],
  credentialNote: '演示账号与密码请联系我们获取',
  contactHint: '说明行业与场景，我们会开通匹配的演示账号并安排一次线上讲解。'
}

/** 首页首屏指标（结构性事实，非经营承诺） */
export const heroFacts = [
  { key: 'lines', value: '4', unit: '大业务线', desc: '智慧农业 · 工业物联网 · MES · 企业服务' },
  { key: 'stack', value: '端-边-云', unit: '一体化', desc: '自终端到平台全链路自研' },
  { key: 'sla', value: '1 工作日', unit: '内响应', desc: '咨询与故障分级响应，值守按合同约定' }
]

/**
 * 四条业务线：路由、主色、文案、能力、场景、交付流程
 */
export const businessLines = [
  {
    key: 'agriculture',
    path: '/agriculture',
    name: '智慧农业',
    en: 'Smart Agriculture',
    accent: '#24e39a',
    accent2: '#22e0ff',
    hero: 'gen/hero-agriculture.jpg',
    cover: 'gen/cover-agriculture.jpg',
    eyebrow: 'Smart Agriculture',
    tagline: '从田间感知到生产决策',
    summary:
      '围绕种植、设施农业与水产养殖场景，构建环境监测、智能控制与生产数据一体的数字农业体系，' +
      '把经验种植变成可量化、可复制的数据化生产。',
    overview:
      '农业的难题往往在“看不见”与“来不及”：温湿度、土壤墒情、光照与水肥状态靠人工巡检，异常发现滞后，' +
      '控制依赖老师傅经验。皓辰以物联网传感器 + 边缘控制柜 + 农业数据平台为主线，' +
      '把环境与设备状态实时搬到看板上，并把灌溉、通风、补光等动作交给规则与策略执行。',
    pains: [
      { title: '靠天吃饭，数据看不见', desc: '环境与土壤数据靠人工巡检，采样点少、滞后大，问题发现时已造成损失。' },
      { title: '经验难复制，管理成本高', desc: '水肥、通风、补光全凭师傅经验，人员一变动，产量与品质就波动。' },
      { title: '设备孤岛，各管一段', desc: '传感器、控制器、水肥机分属不同厂商，数据不通，无法形成闭环。' }
    ],
    capabilities: [
      { icon: 'thermometer', title: '环境监测', desc: '空气温湿度、光照、CO₂、土壤温湿度与墒情多参数实时采集。', tags: ['多参数采集', '分钟级上报', '阈值告警'] },
      { icon: 'drop', title: '智能灌溉与水肥一体', desc: '按墒情与作物生育期自动决策灌溉量与水肥配比，支持远程一键控制。', tags: ['墒情联动', '定量控制', '远程手动'] },
      { icon: 'sun', title: '设施环境调控', desc: '通风、卷帘、补光、加温设备联动控制，支持分段策略与场景一键切换。', tags: ['设备联动', '策略编排', '场景模式'] },
      { icon: 'wifi', title: '农业物联网接入', desc: '支持 Modbus、MQTT、LoRa 等协议接入，兼容主流厂商传感器与控制器。', tags: ['协议解析', '4G/以太网', '离线缓存'] },
      { icon: 'chart', title: '农事与产量分析', desc: '农事记录、投入品与产量数据关联分析，形成可复制的种植模型。', tags: ['农事台账', '投入分析', '模型沉淀'] },
      { icon: 'shield', title: '质量溯源与预警', desc: '关键节点数据留痕，生产批次可追溯，异常自动推送责任人。', tags: ['批次追溯', '异常预警', '责任到人'] }
    ],
    scenes: [
      { title: '设施农业大棚', desc: '温湿度、光照与水肥一体化联动，一人管理多棚。', img: 'gen/scene-greenhouse.jpg' },
      { title: '大田种植', desc: '墒情监测与小气候站组网，指导灌溉与农事作业窗口。', img: 'gen/scene-field.jpg' },
      { title: '水产养殖', desc: '溶解氧、水温、pH 在线监测，增氧设备自动联动，降低泛塘风险。', img: 'gen/scene-aquaculture.jpg' },
      { title: '墒情与气象监测', desc: '分层土壤墒情与小型气象站组网，为灌溉决策提供现场依据。', img: 'gen/agri-sensor.jpg' },
      { title: '水肥一体灌溉', desc: '按配方定量注肥、按时段分区轮灌，用水用肥有台账。', img: 'gen/agri-waterfert.jpg' },
      { title: '掌上农事', desc: '手机端看数据、收告警、填农事记录，棚里棚外一个入口。', img: 'gen/agri-app.jpg' }
    ],
    arch: [
      { layer: '现场层', color: '#24e39a', items: ['空气温湿度', '土壤墒情', '光照/CO₂', '水泵阀门', '风机卷帘'] },
      { layer: '边缘层', color: '#22e0ff', items: ['RTU 采集', '协议解析', '本地规则', '断网续传', '离线联动'] },
      { layer: '平台层', color: '#3d7bff', items: ['设备管理', '数据看板', '策略中心', '告警工单', '农事台账'] }
    ],
    flow: [
      { title: '现场勘查', desc: '地块、作物、现有设备与网络条件摸底' },
      { title: '方案设计', desc: '点位布设、控制策略与网络方案输出' },
      { title: '安装调试', desc: '传感器与控制器安装、联调与标定' },
      { title: '上线培训', desc: '平台交付、策略配置与现场人员培训' },
      { title: '持续运维', desc: '远程值守、策略优化与季节复盘' }
    ],
    /* 平台功能矩阵：比“核心能力”更细，按模块给出功能点 */
    modules: [
      {
        name: '感知与采集',
        icon: 'thermometer',
        items: [
          '空气温湿度、光照、CO₂ 多参数采集',
          '土壤温湿度与分层墒情',
          '小型气象站（风速、雨量、辐射）',
          '水体溶氧、pH、水温等水质参数',
          '采集频率与上报周期按点位配置',
          '断网缓存与恢复后续传，不丢数据'
        ]
      },
      {
        name: '控制与执行',
        icon: 'gear',
        items: [
          '水泵、阀门定时与定量控制',
          '水肥一体配比下发与轮灌计划',
          '风机、湿帘、卷帘、补光联动',
          '分棚分地块独立策略，互不干扰',
          '手动优先：现场操作可随时接管',
          '操作留痕与执行回执可查'
        ]
      },
      {
        name: '策略与告警',
        icon: 'bell',
        items: [
          '多级阈值告警（提醒 / 预警 / 紧急）',
          '昼夜与时段的差异化策略',
          '联动规则：条件触发多设备动作',
          '告警分级派单到责任人',
          '短信 / 微信 / 电话多通道通知',
          '误报抑制、告警静默与确认闭环'
        ]
      },
      {
        name: '农事与追溯',
        icon: 'clipboard',
        items: [
          '农事台账：播种、施肥、打药、采收',
          '投入品用量与批次记录',
          '地块 / 批次 / 责任人关联',
          '二维码溯源，扫码看全过程',
          '产量与投入对比分析',
          '可复制的种植模型沉淀'
        ]
      },
      {
        name: '看板与报表',
        icon: 'chart',
        items: [
          '实时总览与地块视图',
          '历史趋势与跨棚对比',
          '水肥、用电等投入统计',
          '日报 / 周报自动生成',
          '数据导出与第三方系统对接'
        ]
      }
    ],
    /* 交付物清单：让客户知道“买到什么” */
    deliverables: [
      { group: '系统', items: ['农业物联网平台（Web / 移动端）', '边缘网关与 RTU 固件', '实时看板与数据大屏'] },
      { group: '硬件', items: ['传感器与小型气象站', '控制柜与执行机构', '供电与网络配套'] },
      { group: '文档', items: ['点位表与组态清单', '控制策略说明', '操作手册与培训材料', '竣工与验收资料'] },
      { group: '服务', items: ['安装调试与标定', '平台部署与联调', '现场培训', '运维值守与策略优化'] }
    ],
    /* 建设成效：方向性成果，具体数值以现场实测为准 */
    outcomes: [
      { title: '数据由人工抄表转为在线', desc: '环境数据分钟级更新，超限即报，问题从“事后发现”变为“及时处置”。' },
      { title: '灌溉与通风按策略自动执行', desc: '常见动作不再依赖人工记忆，减少遗漏与重复劳动，一人可兼顾多个棚。' },
      { title: '水肥投入可计量可对比', desc: '用水用肥形成台账，为节水节肥与成本核算提供依据。' },
      { title: '生产过程留痕可追溯', desc: '关键节点数据与农事记录对应，质量追溯和责任界定有据可查。' }
    ],
    /* 适用对象 */
    roles: ['种植大户与生产基地', '农业合作社', '现代农业园区', '农技服务单位', '涉农监管部门'],
    outcomeImg: 'gen/agri-app.jpg',
    outcomeCaption: '手机端看数据、收告警、记农事，棚里棚外共用一套数据',
    faq: [
      {
        q: '大棚里已有部分传感器和控制器，能接入吗？',
        a: '支持 Modbus RTU/TCP 等主流协议接入。实施前先做协议与点表盘点，能读的读、能控的控；确实不对外开放的设备保留原有控制方式，仅做数据采集，不推翻现有可用系统。'
      },
      {
        q: '现场网络不稳定，会不会影响控制？',
        a: '关键联动策略运行在边缘侧，断网时本地照常执行，网络恢复后自动续传数据；云平台负责策略下发与展示，不作为现场控制的唯一通道。'
      },
      {
        q: '需要农艺人员长期配合吗？',
        a: '建设期需要农艺人员参与确定阈值与策略，这是项目能不能用起来的关键；上线后日常以平台操作为主，策略由我方结合季节与生长周期复盘调整。'
      },
      {
        q: '多久能见到效果？',
        a: '单点采集与控制、看板类功能在现场调试完成后即可使用；联动策略与种植模型需要经历一个完整生长周期校准，效果逐步显现。'
      },
      {
        q: '数据归谁所有，安全怎么保障？',
        a: '数据存储在客户指定环境（本地或云），账号分级授权、操作留痕，不向第三方共享生产数据；如需对接监管平台，由客户授权后按接口规范报送。'
      }
    ],

    /* 选型三档：帮客户自我定位，周期为经验区间（非承诺），具体以勘查后的方案排期为准 */
    plans: [
      {
        key: 'monitor',
        name: '基础监测档',
        fit: '先把棚里的数据看清楚',
        cycle: '1–3 周',
        items: [
          '环境多参数采集（温湿度、光照、CO₂）',
          '土壤温湿度与分层墒情采集',
          '阈值告警推送到手机与看板',
          '实时总览与历史趋势查询',
          '单棚 / 单地块试点，验证后再扩'
        ]
      },
      {
        key: 'control',
        name: '控制联动档',
        fit: '把灌溉、通风交给策略执行',
        cycle: '3–6 周',
        items: [
          '含基础监测档全部内容',
          '水泵、阀门定时与定量控制',
          '水肥一体配方下发与轮灌计划',
          '风机、湿帘、卷帘、补光联动',
          '昼夜时段差异化策略 + 手动优先接管'
        ]
      },
      {
        key: 'intel',
        name: '数据智能档',
        fit: '沉淀可复制的种植模型',
        cycle: '6–12 周',
        items: [
          '含控制联动档全部内容',
          '农事台账与投入品批次记录',
          '二维码溯源，扫码看全过程',
          '产量与投入对比分析',
          '多棚多基地统一看板与自动报表'
        ]
      }
    ],

    /* 计价与报价口径（四条业务线统一口径，避免只在一处说明） */
    pricing: {
      basis: '按点位数量、协议复杂度与控制范围报价，软件功能按模块拆分计数',
      notes: [
        '先勘查再报价：现场项目不远程拍价，方案中写清点位、功能范围与验收口径',
        '可分批实施：先做试点档位，验证效果后再扩展到其他棚区或基地',
        '报价单与方案清单同步给出，明确哪些包含、哪些不含（如供电、网络、机柜改造）'
      ]
    },

    /* 与五步流程一一对应的经验周期 */
    cycle: {
      total: '2–8 周（视档位与点位数量）',
      note: '以下为同类项目的经验区间，具体排期以勘查后的方案为准',
      stages: ['2–3 天', '3–5 天', '1–3 周', '1–2 天', '持续']
    }
  },
  {
    key: 'iot',
    path: '/iot',
    name: '工业物联网',
    en: 'Industrial IoT',
    accent: '#22e0ff',
    accent2: '#3d7bff',
    hero: 'gen/hero-iot.jpg',
    cover: 'gen/cover-iot.jpg',
    eyebrow: 'Industrial IoT',
    tagline: '让设备说话，让产线透明',
    summary:
      '打通异构设备与产线数据，从协议解析、边缘采集到设备管理与预测性维护，' +
      '把“看不见的生产过程”变成实时可视、可控、可优化的数据资产。',
    overview:
      '工厂里最贵的成本是“停机”和“黑箱”：设备品牌各异、协议不通，故障靠人发现，能耗靠事后对账。' +
      '皓辰以边缘网关 + 工业数据平台为主线，完成设备接入、状态感知、远程控制与能耗分析，' +
      '并进一步叠加规则引擎与预测模型，让维护从被动抢修转向计划性保养。',
    pains: [
      { title: '设备协议五花八门', desc: '不同品牌 PLC、仪表与变频器协议各异，数据接不上来，改造一拖再拖。' },
      { title: '故障被动抢修', desc: '停机后才排查，缺少趋势与预警，单次故障损失直接落在产能上。' },
      { title: '能耗与产量对不上', desc: '电表、气表与生产数据分离，单位产品能耗算不清，节能缺依据。' }
    ],
    capabilities: [
      { icon: 'link', title: '设备接入与协议解析', desc: '支持 Modbus RTU/TCP、OPC UA、MQTT、DL/T645 等主流协议，兼容存量设备。', tags: ['多协议', '存量改造', '免停机接入'] },
      { icon: 'cpu', title: '边缘采集与控制', desc: '边缘网关本地采集、本地规则、断网续传，关键控制不依赖云端。', tags: ['边缘计算', '断网可用', '毫秒级响应'] },
      { icon: 'activity', title: '产线状态监测', desc: '设备运行、停机、节拍与工艺参数实时看板，异常一屏可见。', tags: ['实时看板', '节拍分析', '异常定位'] },
      { icon: 'gear', title: '远程控制与定时任务', desc: '设备启停、参数下发与定时策略统一管理，操作留痕可追溯。', tags: ['远程控制', '定时任务', '操作留痕'] },
      { icon: 'target', title: '预测性维护', desc: '基于电流、振动、温度趋势建模，提前识别劣化，按状态安排保养。', tags: ['趋势预警', '保养计划', '备件预判'] },
      { icon: 'zap', title: '能耗与能效管理', desc: '分项计量与单位产品能耗分析，定位高耗能环节并输出优化建议。', tags: ['分项计量', '单耗分析', '节能建议'] }
    ],
    scenes: [
      { title: '离散制造产线', desc: '设备联网与节拍分析，找瓶颈工位，提升 OEE。', img: 'gen/scene-factory.jpg' },
      { title: '设备远程运维', desc: '分散设备集中纳管，故障远程诊断、参数远程下发。', img: 'gen/scene-remoteops.jpg' },
      { title: '能源与环保监测', desc: '水电气热分项计量与排放数据在线监测。', img: 'gen/scene-energy.jpg' },
      { title: '存量设备协议接入', desc: '网关与采集模块就地取数，RS485 / 以太网混合组网。', img: 'gen/iot-gateway.jpg' },
      { title: '工艺参数与质量关联', desc: '加工参数与质量数据关联，异常批次快速回溯。', img: 'gen/iot-cnc.jpg' },
      { title: '巡检与预测性维护', desc: '红外测温、振动与电流趋势，提前安排保养窗口。', img: 'gen/iot-inspect.jpg' }
    ],
    arch: [
      { layer: '设备层', color: '#22e0ff', items: ['PLC/仪表', '变频器', '电机泵阀', '电表气表', '传感器'] },
      { layer: '边缘层', color: '#3d7bff', items: ['边缘网关', '协议解析', '本地规则', '断网续传', '边缘控制'] },
      { layer: '平台层', color: '#8b5cf6', items: ['设备台账', '实时监控', '规则引擎', '预测维护', '能耗分析'] }
    ],
    flow: [
      { title: '设备盘点', desc: '梳理设备清单、协议与网络现状' },
      { title: '接入方案', desc: '确定网关部署、点表与采集频率' },
      { title: '接入调试', desc: '协议联调、点表校验与数据核对' },
      { title: '应用上线', desc: '看板、规则与告警配置上线' },
      { title: '运维优化', desc: '持续巡检、模型调优与功能迭代' }
    ],
    modules: [
      {
        name: '设备接入',
        icon: 'link',
        items: [
          'Modbus RTU / TCP、OPC UA、MQTT',
          'DL/T645 电表与常见仪表协议',
          '西门子、三菱、欧姆龙等主流 PLC',
          '私有协议适配与厂商对接',
          '点表批量导入、导入前校验',
          '旁路接入，尽量不动原有控制逻辑'
        ]
      },
      {
        name: '数据采集',
        icon: 'activity',
        items: [
          '采集周期与变化死区按点位配置',
          '变化上报 + 周期兜底上报',
          '设备时间同步与数据补正',
          '断网本地缓存、恢复自动续传',
          '数据质量标记（离线 / 异常 / 正常）',
          '原始数据留存，支持事后复算'
        ]
      },
      {
        name: '监控与告警',
        icon: 'chart',
        items: [
          '设备实时状态：运行 / 停机 / 故障',
          '工艺参数趋势与多设备对比',
          '分级告警与升级策略',
          '值班分派与责任人到岗',
          '告警确认、处理记录与闭环统计',
          '车间看板与大屏投放'
        ]
      },
      {
        name: '控制与计划',
        icon: 'gear',
        items: [
          '远程启停、参数下发与回读校验',
          '定时任务与班次策略',
          '联锁条件与权限校验，防误操作',
          '下发失败重试与执行回执',
          '全量操作审计，可追溯是谁下的指令'
        ]
      },
      {
        name: '分析与优化',
        icon: 'target',
        items: [
          '节拍与 OEE 统计，定位瓶颈工位',
          '单位产品能耗与分项计量',
          '电流、振动、温度趋势建模',
          '劣化预警与保养计划生成',
          '备件预判与采购建议',
          '节能与工艺优化建议输出'
        ]
      }
    ],
    deliverables: [
      { group: '系统', items: ['工业物联网平台', '边缘网关固件与配置工具', '设备台账与点表管理', '实时监控与能效看板'] },
      { group: '硬件', items: ['边缘网关与采集模块', '信号转换与隔离器件', '机柜、供电与网络改造', '传感器与智能仪表'] },
      { group: '文档', items: ['点表与协议对照表', '网络拓扑与 IP 规划', '联调与数据校验记录', '运维与故障处理手册'] },
      { group: '服务', items: ['设备盘点与方案设计', '协议联调与点位校验', '上线陪跑与值守', '巡检、模型调优与迭代'] }
    ],
    outcomes: [
      { title: '设备状态从看不见到在线可视', desc: '停机与异常定位缩短到分钟级，问题在造成批量损失前被发现。' },
      { title: '控制有回执、操作有留痕', desc: '参数下发与启停动作记录完整，规避口头指令带来的误操作。' },
      { title: '能耗按设备与产线分项可管', desc: '单位产品能耗可比可算，为节能改造提供数据依据。' },
      { title: '维护从抢修转向计划性保养', desc: '趋势预警替代事后维修，减少非计划停机对交付的影响。' }
    ],
    roles: ['离散制造企业', '流程工业与公用工程', '设备厂商与运维服务商', '园区与能源管理单位'],
    outcomeImg: 'gen/iot-inspect.jpg',
    outcomeCaption: '巡检数据与趋势分析结合，保养按设备状态安排',
    faq: [
      {
        q: '老设备没有网口，还能接入吗？',
        a: '能。带 RS485/RS232 的设备通过网关或采集模块接入；只有硬接线的老设备可先用 I/O 采集运行/停机状态，读不到的参数用外置仪表补齐，不要求设备本身具备联网能力。'
      },
      {
        q: '施工需要停机吗？',
        a: '采集侧多数可在运行中完成接线与调试；仅在需要串入控制回路或加装互感器时安排分段停机窗口，与生产计划协商后执行。'
      },
      {
        q: '控制权限和责任怎么划分？',
        a: '平台只开放双方确认过的控制点，敏感操作可要求二次确认或审批；也可以先只做监视不下发控制，待运行稳定后按阶段放开控制权限。'
      },
      {
        q: '数据保留多久？能不能对接现有系统？',
        a: '原始数据与统计数据的保留周期按客户要求配置，历史趋势可导出。与 MES / ERP 通过接口或中间库对接，实施前先统一设备编码、工单号等主数据口径，避免形成第二套台账。'
      },
      {
        q: '投入怎么算？多久见效？',
        a: '投入主要取决于接入设备数量、协议复杂度与控制范围，按点位与功能模块报价。看板与监视类功能在调试完成后即可使用，节能与预测类需要积累一段运行数据才能给出有效结论。'
      }
    ],

    /* 选型三档：按产线范围与目标选档，周期为经验区间（非承诺） */
    plans: [
      {
        key: 'monitor',
        name: '基础监测档',
        fit: '先让设备状态在线可视',
        cycle: '1–3 周',
        items: [
          'Modbus RTU/TCP、OPC UA、MQTT 协议接入',
          '设备实时状态看板（运行 / 停机 / 故障）',
          '分级告警与值班分派到责任人',
          '数据质量标记与原始数据留存',
          '单条产线或 20 个点位以内试点'
        ]
      },
      {
        key: 'control',
        name: '控制联动档',
        fit: '把远程控制与定时任务管起来',
        cycle: '3–6 周',
        items: [
          '含基础监测档全部内容',
          '远程启停与参数下发（回读校验 + 执行回执）',
          '定时任务与班次策略',
          '联锁条件与权限校验，防误操作',
          '全量操作审计 + 车间看板 / 大屏'
        ]
      },
      {
        key: 'intel',
        name: '数据智能档',
        fit: '从被动抢修转向计划性保养',
        cycle: '6–12 周',
        items: [
          '含控制联动档全部内容',
          '节拍与 OEE 统计，定位瓶颈工位',
          '单位产品能耗与分项计量',
          '电流、振动、温度趋势建模',
          '劣化预警、保养计划与备件预判'
        ]
      }
    ],

    pricing: {
      basis: '按接入设备数量、协议复杂度与控制范围报价，软件功能按模块拆分计数',
      notes: [
        '先勘查再报价：先做设备与协议盘点，再给出点位表与报价',
        '旁路优先：采集侧尽量不停机，需要串入控制回路时再协商停机窗口',
          '可先只做监视、暂不下发控制，运行稳定后按阶段放开控制权限'
      ]
    },

    cycle: {
      total: '2–10 周（视档位与设备数量）',
      note: '以下为同类项目的经验区间；数据智能档需在接入后积累一段运行数据，模型结论才有效',
      stages: ['2–3 天', '3–5 天', '1–3 周', '1–2 天', '持续']
    }
  },
  {
    key: 'enterprise',
    path: '/enterprise',
    name: '企业服务',
    en: 'Enterprise Services',
    accent: '#8b5cf6',
    accent2: '#22e0ff',
    hero: 'gen/hero-enterprise.jpg',
    cover: 'gen/cover-enterprise.jpg',
    eyebrow: 'Enterprise Services',
    tagline: '把业务需求，做成能用的系统',
    summary:
      '面向政企与中小企业提供数字化咨询、定制软件开发、数据平台建设与运维保障，' +
      '从需求梳理到上线交付全程负责，不交付“半成品”。',
    overview:
      '很多数字化项目失败不是因为技术不够，而是需求没有对齐、交付没有闭环。' +
      '皓辰以“需求共创—小步交付—上线验收—持续运维”的方式推进企业服务：' +
      '先和企业把业务场景讲清楚，再用可运行的系统逐步替换人工与表格，最后用运维和数据把效果守住。',
    pains: [
      { title: '系统买了用不起来', desc: '通用软件套不进自身流程，员工回到表格与微信，系统成了摆设。' },
      { title: '数据散落各处', desc: '业务、财务、生产数据分散在不同系统，报表靠人工拼，口径对不上。' },
      { title: '上线即失联', desc: '项目交付后缺少运维与迭代支持，需求变化没人接。' }
    ],
    capabilities: [
      { icon: 'target', title: '数字化咨询与规划', desc: '业务现状诊断、数字化蓝图与实施路径设计，先算清投入产出的账。', tags: ['现状诊断', '路径规划', '投入产出'] },
      { icon: 'code', title: '定制软件开发', desc: '按业务流程定制管理系统、移动应用与小程序，贴合一线操作习惯。', tags: ['管理系统', '移动端', '流程贴合'] },
      { icon: 'database', title: '数据平台与 BI', desc: '多源数据归集治理、指标体系与可视化看板，报表口径统一。', tags: ['数据归集', '指标口径', '经营看板'] },
      { icon: 'layers', title: '系统集成对接', desc: '打通既有 ERP、财务、设备与第三方平台，避免重复录入。', tags: ['接口对接', '单点登录', '数据同步'] },
      { icon: 'users', title: '驻场实施与培训', desc: '提供现场实施、数据迁移与分层培训，确保系统真正被使用。', tags: ['现场实施', '数据迁移', '分层培训'] },
      { icon: 'shield', title: '运维保障与安全', desc: '值守与巡检按合同 SLA 执行：故障分级响应、定期巡检与安全加固。', tags: ['分级响应', '定期巡检', '安全加固'] }
    ],
    scenes: [
      { title: '政企数字化平台', desc: '业务审批、数据报送与监管看板的一体化建设。', img: 'gen/scene-gov.jpg' },
      { title: '中小企业上云', desc: '轻量化业务系统与经营数据看板，低成本起步。', img: 'gen/scene-smb.jpg' },
      { title: '数据治理与报表', desc: '统一数据口径，把分散报表变成可追溯的经营数仓。', img: 'gen/scene-data.jpg' },
      { title: '需求共创与方案确认', desc: '先对齐业务流程与验收标准，再进入开发，减少返工。', img: 'gen/ent-workshop.jpg' },
      { title: '系统集成与部署', desc: '机房 / 云环境部署，与既有系统对接联调。', img: 'gen/ent-server.jpg' },
      { title: '现场实施与陪跑', desc: '驻场实施、数据迁移与分层培训，确保用起来。', img: 'gen/ent-onsite.jpg' }
    ],
    arch: [
      { layer: '业务层', color: '#8b5cf6', items: ['业务系统', '移动应用', '经营看板', '审批流程', '对外门户'] },
      { layer: '平台层', color: '#3d7bff', items: ['统一认证', '数据中台', '接口网关', '消息通知', '权限体系'] },
      { layer: '支撑层', color: '#22e0ff', items: ['服务器/云', '数据库', '备份容灾', '日志监控', '安全加固'] }
    ],
    flow: [
      { title: '需求访谈', desc: '走进业务现场，梳理真实流程与痛点' },
      { title: '方案报价', desc: '确定范围、里程碑与验收标准' },
      { title: '迭代开发', desc: '小步交付，关键节点演示确认' },
      { title: '上线验收', desc: '数据迁移、并行运行与验收确认' },
      { title: '运维迭代', desc: '响应支持、功能演进与年度复盘' }
    ],
    modules: [
      {
        name: '咨询与规划',
        icon: 'target',
        items: [
          '现状访谈与业务流程梳理',
          '在用的系统、数据与设备盘点',
          '痛点量化与优先级排序',
          '数字化蓝图与分期实施路径',
          '投入产出测算与选型建议',
          '招标技术文件与评审支持'
        ]
      },
      {
        name: '定制开发',
        icon: 'code',
        items: [
          '需求规格与原型确认后开工',
          'Web 管理后台与业务系统',
          '移动端 / 小程序 / 公众号',
          '审批流与表单流程引擎',
          '组织架构、角色与数据权限',
          '版本迭代、灰度发布与回滚方案'
        ]
      },
      {
        name: '数据与报表',
        icon: 'database',
        items: [
          '多源数据归集与清洗',
          '主数据与指标口径治理',
          '数据质量校验与异常提示',
          '经营看板与专题分析',
          '自定义报表、导出与定时报送'
        ]
      },
      {
        name: '集成对接',
        icon: 'layers',
        items: [
          '统一身份认证与单点登录',
          'ERP / 财务 / 生产系统接口',
          '企微、钉钉、短信消息通知',
          '文件、影像与附件管理',
          '政务与监管平台数据报送',
          '接口监控、重试与告警'
        ]
      },
      {
        name: '上线与运维',
        icon: 'shield',
        items: [
          '本地机房或云环境部署',
          '历史数据迁移与并行运行',
          '分层培训与操作考核',
          '定期巡检、备份与安全加固',
          '问题响应与升级机制',
          '年度复盘与下一年度规划'
        ]
      }
    ],
    deliverables: [
      { group: '方案', items: ['需求规格说明书', '原型与界面确认稿', '实施方案与里程碑计划', '验收标准清单'] },
      { group: '系统', items: ['业务系统（Web / 移动端）', '数据看板与报表', '接口与集成中间件', '部署脚本与环境配置'] },
      { group: '文档', items: ['操作手册与培训材料', '运维手册', '数据字典与接口文档', '验收与移交资料'] },
      { group: '服务', items: ['驻场实施与数据迁移', '分层培训与考核', '上线陪跑', '运维值守与功能迭代'] }
    ],
    outcomes: [
      { title: '业务流程从线下转到线上', desc: '审批、报送与台账线上流转，进度与责任可追溯。' },
      { title: '口径统一、重复录入减少', desc: '指标与主数据一处维护，报表不再靠人工拼接。' },
      { title: '与既有系统打通', desc: '避免形成新的信息孤岛，数据在系统间自动流转。' },
      { title: '上线后有承接', desc: '运维与迭代机制明确，需求变化有人接、有节奏。' }
    ],
    roles: ['政府与事业单位', '中小企业', '集团下属公司', '产业园区运营方'],
    outcomeImg: 'gen/ent-onsite.jpg',
    outcomeCaption: '驻场实施与陪跑，确保系统真正被一线用起来',
    faq: [
      {
        q: '预算有限，从哪里开始比较划算？',
        a: '先解决最影响效率的一个流程，用最小可用版本上线验证；整体规划可以一次做清，投入按阶段分批，避免一次性大范围建设带来的风险。'
      },
      {
        q: '开发周期一般多久？',
        a: '取决于功能范围与对接系统数量。经验做法是先做 1～2 个关键流程的试点并跑通数据，再按迭代推进，每个里程碑都有可演示的成果。'
      },
      {
        q: '能不能只做咨询、不做开发？',
        a: '可以。咨询与规划作为独立服务交付，输出蓝图、路径与选型建议，开发可由贵方自有团队或其他供应商执行。'
      },
      {
        q: '源代码和数据归属怎么算？',
        a: '按合同约定执行；默认交付源码、数据库脚本与部署文档，数据存放在客户指定环境，我方不保留生产数据副本。'
      },
      {
        q: '上线之后谁来维护？',
        a: '提供运维值守与功能迭代服务，也可以按客户要求做知识转移，由贵方自有 IT 团队接手，我方提供过渡期支持。'
      }
    ],

    /* 选型三档：按范围与目标选档，周期为经验区间（非承诺） */
    plans: [
      {
        key: 'flow',
        name: '单点流程档',
        fit: '先把最卡的一个流程搬上线',
        cycle: '2–4 周',
        items: [
          '1 个核心流程线上化（表单 + 审批流）',
          '组织架构、角色与数据权限',
          '台账与基础报表、导出',
          '分层培训与操作手册',
          '小范围试点，跑通后再扩大范围'
        ]
      },
      {
        key: 'integration',
        name: '系统集成档',
        fit: '与既有系统打通，口径统一',
        cycle: '4–10 周',
        items: [
          '含单点流程档全部内容',
          '主数据与指标口径治理',
          '与 ERP / 财务 / 设备系统接口对接',
          '统一身份认证与单点登录',
          '经营看板、接口监控与失败告警'
        ]
      },
      {
        key: 'platform',
        name: '数据平台档',
        fit: '把数据变成可追溯的经营资产',
        cycle: '8–16 周',
        items: [
          '含系统集成档全部内容',
          '多源数据归集、清洗与质量校验',
          '专题分析与自定义报表',
          '定时报送与自动分发',
          '年度迭代规划与知识转移'
        ]
      }
    ],

    pricing: {
      basis: '按功能范围、对接系统数量与实施方式（远程 / 驻场）报价',
      notes: [
        '咨询与规划可单独交付：输出蓝图、路径与选型建议，不强制绑定开发',
        '开发按里程碑分期确认：每个关键节点先演示确认，再进入下一阶段',
        '报价单与需求范围同步给出，需求变更按变更单确认，避免无边界追加'
      ]
    },

    cycle: {
      total: '3–14 周（视功能范围与对接系统数量）',
      note: '以下为同类项目的经验区间，具体以需求确认后的里程碑计划为准',
      stages: ['3–5 天', '3–5 天', '2–8 周', '1–2 周', '持续']
    }
  },
  {
    key: 'mes',
    path: '/mes',
    name: 'MES 生产执行系统',
    /* 导航/页脚等横向空间紧张处使用的短名，避免导航折行 */
    navName: 'MES',
    en: 'Manufacturing Execution System',
    accent: '#f2a33c',
    accent2: '#f2d43c',
    hero: 'gen/mes-hero.jpg',
    cover: 'gen/mes-cover.jpg',
    eyebrow: 'Manufacturing Execution System',
    tagline: '让车间执行看得见、管得住、追得回',
    summary:
      '面向离散制造与流程制造车间，把生产订单拆解为车间工单与工序任务，' +
      '管控派工、报工、在制品流转、质检与线边物料，建立从原料批次到成品的正反向追溯链条。',
    overview:
      '车间的成本主要落在两处：切换与等待。多品种小批量、按订单生产时，工单切换频繁、工序流转不透明、' +
      '在制品管控混乱、进度只有到现场问才知道；流程制造则更依赖工艺执行的一致性与批次可追溯。' +
      '皓辰 MES 位于上层计划管理与车间现场执行之间，把工单、工序、在制品、质检与物料串成一条可跟踪的执行链，' +
      '让生产进度与质量责任都有数据可查。',
    pains: [
      { title: '工单切换频繁', desc: '多品种小批量、按订单生产，插单改单靠口头传达，进度只有到车间问才知道。' },
      { title: '工序流转不透明', desc: '在制品处于哪道工序、有多少件，柜子里说不清，积压与丢失同时发生。' },
      { title: '质量追溯断裂', desc: '出了不良要从原料批次一路倒查到成品，记录散在纸质单据里，追溯慢、影响范围说不准。' }
    ],
    capabilities: [
      { icon: 'clipboard', title: '生产调度与工单管理', desc: '承接生产订单，拆解车间工单与工序任务，结合设备产能与人员排班排产派工。', tags: ['排产派工', '插单改单', '进度跟踪'] },
      { icon: 'gear', title: '工艺与工序管控', desc: '固化标准工艺路线、作业规范与质检要求，工位端推送指引并校验工序跳转逻辑。', tags: ['工艺路线', '防跳序漏序', '工位指引'] },
      { icon: 'box', title: '在制品与流转管理', desc: '管控工序转移与批次交接，跟踪每批次所处工序、加工状态与数量，减少积压与丢失。', tags: ['批次交接', '在制分布', '流转效率'] },
      { icon: 'shield', title: '质量管理与追溯', desc: '覆盖首检、巡检、完工检，记录不良类型与返工处理，建立正反向追溯链。', tags: ['全环节质检', '正反向追溯', '不良与返工'] },
      { icon: 'layers', title: '物料与生产协同', desc: '管控线边物料领用、投料与补料，按需拉动并核对物料批次与规格，避免错料缺料。', tags: ['线边物料', '按需拉动', '防错料缺料'] },
      { icon: 'chart', title: '工时核算与绩效分析', desc: '人员工时核算、生产报表统计与绩效数据分析，可按行业特性与生产模式配置。', tags: ['工时核算', '生产报表', '绩效分析'] }
    ],
    scenes: [
      { title: '汽车零部件', desc: '多品种小批量与快速换产，工单与工序流转全程管控。', img: 'gen/mes-scene-auto.jpg' },
      { title: '高端装备装配', desc: '装配全链路追溯，工艺参数与作业规范按工位校验。', img: 'gen/mes-scene-assembly.jpg' },
      { title: '电子组装', desc: '工位端电子化作业指引，拦截跳工序、漏工序、错工艺。', img: 'gen/mes-scene-electronics.jpg' },
      { title: '食品饮料与生物医药', desc: '批次化生产管控与工艺执行固化，满足行业合规与质量管理要求。', img: 'gen/mes-scene-food.jpg' },
      { title: '五金注塑与纺织服装', desc: '解决生产黑箱、排产混乱与效率统计难，实现流程数字化。', img: 'gen/mes-scene-injection.jpg' },
      { title: '质量追溯与不良分析', desc: '从成品倒查到原料批次，一次圈准质量问题的影响范围。', img: 'gen/mes-scene-trace.jpg' }
    ],
    arch: [
      { layer: '计划层', color: '#8b5cf6', items: ['ERP 生产订单', '工艺路线', '物料主数据', '交期计划'] },
      { layer: '执行层', color: '#f2a33c', items: ['工单派工', '工序流转', '报工', '质检', '物料领用'] },
      { layer: '数据层', color: '#22e0ff', items: ['生产追溯', '工时与绩效', '报表与看板', '接口对接'] }
    ],
    flow: [
      { title: '需求调研与蓝图设计', desc: '调研车间流程、管理痛点与业务目标，输出业务蓝图与验收标准' },
      { title: '系统部署与基础配置', desc: '配置组织角色权限，维护物料主数据、工艺路线、工作中心与人员班组' },
      { title: '业务适配与流程配置', desc: '配置工单流转、工序管控、质检规则与物料领用，并与 ERP 等系统对接' },
      { title: '试点验证与优化迭代', desc: '选典型产线试点运行，分层培训并收集反馈迭代优化' },
      { title: '全面推广与持续运维', desc: '全车间上线，建立运维保障机制并深化排产优化与效率分析' }
    ],
    modules: [
      {
        name: '生产调度与工单',
        icon: 'clipboard',
        items: [
          '承接生产订单，拆解车间工单与工序任务',
          '结合设备产能与人员排班排产派工',
          '插单、撤单、改单等异常调整',
          '开工、报工、完工全生命周期管控',
          '生产进度实时跟踪'
        ]
      },
      {
        name: '工艺与工序管控',
        icon: 'gear',
        items: [
          '标准工艺路线与作业规范固化',
          '工位端推送对应作业指引',
          '工序跳转逻辑校验，防跳工序、漏工序',
          '错工艺拦截，与质检要求绑定',
          '工艺变更走变更确认，留痕可追溯'
        ]
      },
      {
        name: '在制品与流转',
        icon: 'box',
        items: [
          '工序转移与批次交接管控',
          '跟踪每批次 / 每件产品所处工序与数量',
          '在制分布与流转效率可视化',
          '减少在制品积压与丢失',
          '关键节点数量核对与差异提醒'
        ]
      },
      {
        name: '质量与追溯',
        icon: 'shield',
        items: [
          '首检、巡检、完工检全覆盖',
          '检验结果、不良类型与返工处理记录',
          '原料批次 → 工序加工 → 成品正反向追溯',
          '快速定位质量问题根源与影响范围',
          '质检记录与工单、批次关联留痕'
        ]
      },
      {
        name: '物料与工时',
        icon: 'layers',
        items: [
          '线边物料领用、投料、补料流程',
          '按需物料拉动，核对物料批次与规格',
          '避免错料、缺料导致生产中断',
          '人员工时核算',
          '生产报表统计与绩效数据分析'
        ]
      }
    ],
    deliverables: [
      { group: '方案', items: ['业务蓝图与流程方案', '功能范围与验收标准清单', '实施里程碑计划', '基础数据梳理模板'] },
      { group: '系统', items: ['MES 生产执行系统', '车间看板 / 大屏', '与 ERP 等系统的接口', '移动端扫码报工应用'] },
      { group: '文档', items: ['操作手册与分层培训材料', '运维手册', '数据字典与接口文档', '基础数据规范（物料 / 工艺 / 工作中心）'] },
      { group: '服务', items: ['需求调研与蓝图设计', '系统部署与基础数据配置', '试点陪跑与分层培训', '全面上线与持续运维'] }
    ],
    outcomes: [
      { title: '生产进度从“问人”变为在线可视', desc: '工单走到哪道工序、卡在哪个环节，看板直接看得到。' },
      { title: '工序按标准走，跳序漏序被拦住', desc: '工艺路线固化在系统里，工位端按指引作业，异常当场提示。' },
      { title: '质量问题可正反向追溯到批次', desc: '从成品倒查到原料批次，影响范围一次圈准，不用翻纸质单据。' },
      { title: '工期与工时数据可算', desc: '工时核算与生产报表自动汇总，绩效与效率分析有数据支撑。' }
    ],
    roles: ['汽车零部件与装备制造', '电子组装', '食品饮料与生物医药', '五金注塑 / 纺织服装'],
    outcomeImg: 'gen/mes-outcome.jpg',
    outcomeCaption: '先做典型产线试点与分层培训，跑通后再全面推广',
    faq: [
      {
        q: 'MES 和 ERP 是什么关系？会不会重复建设？',
        a: 'MES 位于上层计划管理与车间现场执行之间，负责把 ERP 下达的生产订单拆解为车间工单与工序任务，并管到工序、报工、质检与物料领用这一层。两者通过接口或中间库对接，实施前先统一物料、工单号、工序等主数据口径，避免形成第二套台账。'
      },
      {
        q: '老设备没有网口，MES 能用吗？',
        a: '能。MES 主要管“人、机、料、法”的执行过程，报工与质检可在工位端或移动端录入；设备侧数据可以先通过 I/O 采集运行/停机状态，后续按需接入网关补齐参数，不要求设备本身具备联网能力。'
      },
      {
        q: '实施周期要多久？',
        a: '按“业务驱动、总体规划、分步实施”推进，经验周期为 3–6 个月，分五个阶段：需求调研与蓝图设计、系统部署与基础配置、业务适配与流程配置、试点验证与优化迭代、全面推广与持续运维。实际排期以调研后的蓝图与里程碑计划为准。'
      },
      {
        q: '会不会上线之后没人用？',
        a: '项目成功的关键在生产业务部门深度参与、基础数据规范准确、一线操作培训落地到位。我们的做法是先在典型产线试点、分层培训跑通，再向全车间推广，避免一次性铺开带来的返工与抵触。'
      },
      {
        q: '定制化程度怎么控制？',
        a: '建议标准功能优先，合理控制定制化范围；确需定制的部分逐条确认并纳入变更管理，保障系统稳定性与后续可扩展、可升级。'
      }
    ],

    /* 选型三档：按车间范围与目标选档，周期为经验区间（非承诺） */
    plans: [
      {
        key: 'workorder',
        name: '工单执行档',
        fit: '先把工单与报工搬上线',
        cycle: '4–8 周',
        items: [
          '工单拆解、派工与开工 / 报工 / 完工闭环',
          '工位端电子化作业指引',
          '基础质检记录（首检 / 巡检 / 完工检）',
          '生产进度看板与基础报表',
          '单条产线或单一车间试点'
        ]
      },
      {
        key: 'trace',
        name: '质量追溯档',
        fit: '把批次追溯与工序管控管起来',
        cycle: '8–14 周',
        items: [
          '含工单执行档全部内容',
          '标准工艺路线固化与工序跳转校验',
          '线边物料领用 / 投料 / 补料管控',
          '原料批次 → 成品正反向追溯链',
          '不良类型与返工处理记录'
        ]
      },
      {
        key: 'integrated',
        name: '系统集成档',
        fit: '与 ERP 打通，口径统一',
        cycle: '12–24 周',
        items: [
          '含质量追溯档全部内容',
          '与 ERP 等上层系统接口对接',
          '主数据口径统一（物料 / 工单号 / 工序）',
          '工时核算与绩效数据分析',
          '车间看板 / 大屏投放与运维机制'
        ]
      }
    ],

    pricing: {
      basis: '按功能范围、车间与产线数量、对接系统数量与实施方式（远程 / 驻场）报价',
      notes: [
        '先调研再报价：先做车间流程与管理现状调研，输出蓝图与功能范围，再给出报价',
        '分期确认：每个里程碑先演示确认再进入下一阶段，费用按阶段支付',
        '需求变更按变更单确认，避免无边界追加'
      ]
    },

    cycle: {
      total: '3–6 个月（视功能范围、车间数量与对接系统而定）',
      note: '以上为同类项目的经验区间，具体以调研后的业务蓝图与里程碑计划为准',
      stages: ['2–4 周', '3–5 周', '4–8 周', '2–4 周', '持续']
    }
  }
]

export const getLine = (key) => businessLines.find((l) => l.key === key) || null

/**
 * 法律文本（隐私政策 / 服务条款）
 * 内容为官网通用口径，上线前建议按备案与法务意见复核一次。
 */
export const legal = {
  updatedAt: '2026 年 9 月 30 日',
  privacy: {
    title: '隐私政策',
    lead: '本政策说明皓辰科技官网在什么情况下收集你的信息、如何使用与保护这些信息，以及你可以如何行使自己的权利。',
    blocks: [
      {
        h: '一、我们收集哪些信息',
        p: [
          '你在咨询表单中主动填写的信息：称呼、联系电话、公司/单位、电子邮箱、咨询方向、期望上线时间、预算区间、需求描述。',
          '站点访问的基础日志：IP 地址、浏览器类型与版本、访问时间与访问页面，仅用于安全防护与稳定性分析。'
        ]
      },
      {
        h: '二、这些信息用来做什么',
        p: [
          '与你取得联系、了解现场情况并给出方案与报价建议。',
          '在你同意的范围内，跟进项目沟通与必要的服务提醒。',
          '我们不将上述信息用于与你的咨询无关的营销推送。'
        ]
      },
      {
        h: '三、如何保存与保护',
        p: [
          '信息存放在受控的邮箱与内部系统中，仅限对接相关人员访问，并采取权限控制与操作留痕。',
          '我们不向第三方出售、出租或共享你的信息；法律法规要求或经你明确授权的除外（如按监管要求报送）。',
          '信息保存期限为实现上述目的所必需的时间；超出期限后我们会删除或匿名化处理。'
        ]
      },
      {
        h: '四、关于在线演示环境',
        p: [
          '演示环境中的企业与设备数据均为公开测试数据，请勿在其中录入真实生产数据、客户信息或个人敏感信息。',
          '演示账号按申请开通，可在不另行通知的情况下回收或重置。'
        ]
      },
      {
        h: '五、你的权利',
        p: [
          '你可以要求查询、更正或删除你提交的信息，也可以要求我们停止联系。',
          '行使上述权利请通过本页底部联系方式与我们沟通，我们会在合理时间内处理。'
        ]
      },
      {
        h: '六、第三方服务',
        p: [
          '官网当前未接入第三方统计或广告代码。如后续接入，我们会在此说明其名称与用途。'
        ]
      },
      {
        h: '七、联系我们',
        p: ['对本政策有疑问，可通过电话、邮箱或微信与我们联系（见页面底部联系方式）。']
      }
    ]
  },
  terms: {
    title: '服务条款',
    lead: '本页说明本网站内容的使用规则与责任边界。访问或使用本站即表示你已阅读并理解以下内容。',
    blocks: [
      {
        h: '一、网站内容说明',
        p: [
          '站内展示的能力清单、档位配置、周期与功能点属于同类项目的经验说明，不构成对具体项目的承诺。',
          '具体项目范围、周期、报价与验收标准，以双方签署的合同及确认后的方案为准。'
        ]
      },
      {
        h: '二、知识产权',
        p: [
          '站内文字、图片、标识、页面设计及系统界面归海阳皓辰网络科技有限公司所有。',
          '未经书面许可，不得复制、转载、镜像或用于商业用途。'
        ]
      },
      {
        h: '三、在线演示环境使用规则',
        p: [
          '演示环境仅用于功能体验与评估；禁止录入真实生产数据与个人敏感信息。',
          '禁止对演示环境进行压力测试、渗透攻击、批量爬取或任何破坏性操作。',
          '如发现违规使用，我们可暂停或回收相关演示账号。'
        ]
      },
      {
        h: '四、站外链接',
        p: ['本站可能包含第三方网站链接，其内容与服务由相应运营方负责，我们不对其内容承担责任。']
      },
      {
        h: '五、责任范围',
        p: [
          '我们会尽力保证站内信息准确、站点可用，但不保证信息完全无误差。',
          '对因使用本站信息而产生的间接损失，我们在法律允许的范围内不承担责任。'
        ]
      },
      {
        h: '六、适用法律',
        p: ['本条款适用中华人民共和国法律。因本站内容产生的争议，双方先友好协商解决。']
      },
      {
        h: '七、联系我们',
        p: ['对本站内容或条款有疑问，可通过页面底部的电话、邮箱或微信与我们联系。']
      }
    ]
  }
}


/** 首页技术底座 */
export const techBase = [
  { icon: 'cpu', title: '端侧感知', desc: '自研 RTU 与传感器接入，兼容 Modbus / MQTT / LoRa 等主流协议。' },
  { icon: 'layers', title: '边缘计算', desc: '边缘网关本地决策与断网续传，关键控制不受网络波动影响。' },
  { icon: 'link', title: '统一接入', desc: '一套平台同时纳管农业设备与工业设备，点表与模型可复用。' },
  { icon: 'database', title: '数据中台', desc: '时序数据与业务数据统一存储，指标口径清晰、可追溯。' },
  { icon: 'spark', title: '智能算法', desc: '规则引擎与趋势模型结合，从告警走向预测与优化建议。' },
  { icon: 'shield', title: '安全运维', desc: '分级权限、操作留痕，值守与响应按合同约定，系统长期稳定运行。' }
]

/** 首页行业场景 */
export const homeScenes = [
  { title: '种植与设施农业', desc: '环境监测、智能灌溉与水肥一体，一人管理多棚。', line: 'agriculture' },
  { title: '水产与畜牧', desc: '溶氧、水温在线监测与增氧联动，降低生产风险。', line: 'agriculture' },
  { title: '工厂设备联网', desc: '存量设备免停机接入，产线状态与节拍透明可视。', line: 'iot' },
  { title: '能源与设备运维', desc: '分项计量、趋势预警与远程控制，把抢修变成保养。', line: 'iot' },
  { title: '政企数字化平台', desc: '业务流程线上化，数据报送与监管看板一体化。', line: 'enterprise' },
  { title: '中小企业经营看板', desc: '低成本起步，把分散台账变成经营数据资产。', line: 'enterprise' }
]

/** 首页为什么选择皓辰 */
export const whyUs = [
  { icon: 'gear', title: '懂现场，再谈技术', desc: '方案从地块与产线出发，先解决一线真正卡住的环节，再谈平台与算法。' },
  { icon: 'code', title: '全栈自研，响应更快', desc: '终端、边缘、平台与前端同一支团队，问题定位不推诿、迭代不等待。' },
  { icon: 'check', title: '交付即闭环', desc: '以可运行的系统与现场验收为交付标准，不交付演示版本。' },
  { icon: 'clock', title: '长期陪伴式服务', desc: '上线不是结束，运维、策略优化与季节/年度复盘持续跟进。' }
]

/** 关于我们 - 发展历程 */
export const milestones = [
  { year: '2025.06', title: '公司成立', desc: '海阳皓辰网络科技有限公司于 2025 年 6 月 9 日成立，注册地山东省烟台市海阳市。' },
  { year: '当前', title: '四条业务线成型', desc: '形成智慧农业、工业物联网、MES 生产执行系统、企业服务四条业务线，具备从终端感知、边缘控制到平台应用的端到端交付能力。' },
  { year: '当前', title: '持续迭代', desc: '平台能力与行业方案按项目节奏持续迭代，以现场实测与验收记录作为成效依据。' }
]

/** 关于我们 - 价值观 */
export const values = [
  { icon: 'target', title: '以现场为准', desc: '所有方案都要能在一线跑通，指标由现场数据说话。' },
  { icon: 'shield', title: '务实与长期主义', desc: '不追概念，按业务节奏交付，用长期服务守住效果。' },
  { icon: 'users', title: '客户成功即我们成功', desc: '客户的生产指标改善，才是项目真正的验收标准。' }
]

/** 技术栈（首页/关于页滚动展示） */
export const techStack = [
  'Vue', 'Java / Spring Boot', 'MySQL', 'Redis', 'TDengine', 'MQTT', 'Modbus', 'OPC UA',
  'LoRa', '4G / 5G', 'Docker', 'Nginx', 'Linux', 'MicroPython', 'RESTful API', 'WebSocket'
]
